<template>
  <div class="app-date-select">
    <input
      type="text"
      :class="['form-control date-control', { 'is-invalid': isTouched && (!computedIsValid || !isValid), 'form-control-sm': small }]"
      @focus="disabled || (calendarEnabled = true)"
      @blur="onUnFocusClose"
      @keyup.enter="apply"
      @change="onChange"
      :value="dateFormatted"
      tabindex="0"
      ref="input"
      :disabled="disabled"
    />
    <div class="app-calendar" v-if="calendarEnabled" ref="calendar">
      <div class="top-bar">
        <div class="month">{{ firstDayOfActiveMonth.format('MMMM YY') }}</div>
        <div class="controls ml-auto">
          <i class="ion ion-md-arrow-back cursor-pointer" @click="subMonth" @mousedown.prevent></i>
          <i class="ion ion-md-arrow-forward cursor-pointer ml-3" @click="plusMonth" @mousedown.prevent></i>
        </div>
      </div>
      <AppCalendarTable
        :first-day-of-active-month="firstDayOfActiveMonth"
        :active-date="date_"
        :marks-map="marksMap"
        :marks-format="marksFormat"
        :is-date-selectable="isDateInValidRange"
        @select="select"
      />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import AppCalendarTable from '@/components/shared/AppCalendarTable'
import { localeDateFormat } from '@/utils/Helpers'

export default {
  name: 'AppDateSelect',
  components: {
    AppCalendarTable
  },
  props: {
    /**
     * Minimum available date
     * Expect date.js object
     */
    minDate: {
      type: Object,
      default: () => dayjs()
    },
    maxDate: {
      type: Object
    },
    date: {
      type: Object,
      default: null
    },
    viewFormat: {
      type: String,
      default: ''
    },
    isValid: {
      type: Boolean,
      default: true
    },
    small: {
      type: Boolean,
      default: false
    },
    marksMap: {
      type: Map,
      default: () => {
        return new Map()
      }
    },
    marksFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    firstDayOfActiveMonth: dayjs().date(1),
    calendarEnabled: false,
    date_: null
  }),
  mounted() {
    this.firstDayOfActiveMonth = (this.date ?? dayjs()).date(1)
  },
  computed: {
    dateFormat() {
      return this.viewFormat === '' ? localeDateFormat() : this.viewFormat
    },
    dateFormatted() {
      if (this.date_) {
        return this.date_?.format(this.dateFormat)
      }
      return ''
    },
    isSemanticValid() {
      return this.date_?.isValid()
    },
    computedIsValid() {
      let valid = this.isSemanticValid
      // The two bounds do not mean the same thing, and DeskEventEditor hangs both
      // on one field:
      //   maxDate is a horizon ("no later than this day" - now + booking limit),
      //   so the whole of that last day has to stay bookable -> compare by day.
      //   minDate is a real lower bound ("no earlier than this moment" - the
      //   booking's own start), so an end before its start has to read invalid
      //   even on the same day -> compare by minute.
      if (valid && this.maxDate) {
        valid = this.date_.isSameOrBefore(this.maxDate, 'date')
      }
      if (valid && this.minDate) {
        valid = this.date_.isSameOrAfter(this.minDate, 'minute')
      }

      return valid
    },
    isTouched() {
      return this.date_
    }
  },
  methods: {
    onChange(e) {
      if (e.target.value) {
        this.date_ = dayjs(e.target.value, this.dateFormat)
      }
    },
    apply() {
      if (this.computedIsValid) {
        this.$emit('change', this.date_)
      }
    },
    onUnFocusClose() {
      this.calendarEnabled = false
      this.apply()
    },
    plusMonth() {
      this.firstDayOfActiveMonth = this.firstDayOfActiveMonth.add(1, 'month')
    },
    subMonth() {
      this.firstDayOfActiveMonth = this.firstDayOfActiveMonth.subtract(1, 'month')
    },
    // Calendar cells are whole days: a day stays selectable when any minute of it
    // is allowed, so both bounds are compared by day here. That is deliberately
    // looser than computedIsValid - picking minDate's own day is allowed, and the
    // field then flags the result until the time picker is moved past minDate.
    isDateInValidRange(cellDay) {
      let valid = true
      if (this.minDate) {
        valid = cellDay.date.isSameOrAfter(this.minDate, 'date')
      }
      if (valid && this.maxDate) {
        valid = cellDay.date.isSameOrBefore(this.maxDate, 'date')
      }
      return valid
    },
    select(cellDay) {
      if (this.isDateInValidRange(cellDay)) {
        this.date_ = cellDay.date
          .set('hour', (this.date_ ?? cellDay.date).get('hour'))
          .set('minute', (this.date_ ?? cellDay.date).get('minute'))
          .set('second', 0)
        this.$refs.input.blur()
      }
    }
  },
  watch: {
    date: {
      immediate: true,
      handler(date) {
        if (date !== null && !dayjs.isDayjs(date)) {
          throw new Error('Date prop should be null or Date.js object')
        }

        this.date_ = date
      }
    }
  }
}
</script>

<style scoped lang="scss">
.top-bar {
  display: flex;
  justify-items: center;
  padding: 0 21px 0 7px;

  & .month {
    font-weight: bold;
  }
}

.app-calendar {
  min-width: 230px;
  position: absolute;
  padding: 10px;
  background: #fff;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
  z-index: 1;
}

.date-control {
  padding-left: 10px;
  padding-right: 10px;
}
</style>
